/**
 * MetaInfoConnectionGroup — one collapsible connection card in the Settings
 * → Meta Info view, wrapping all of that connection's database cards.
 */

import { ChevronDown, ChevronRight } from 'lucide-react';
import { MetaInfoDatabaseCard } from './MetaInfoDatabaseCard';
import type {
  DatabaseMetaInfo,
  EnumColumnDeclaration,
  JsonColumnDeclaration,
  LooseRelationship,
  SpatialColumnDeclaration,
} from '@/types/electron';

export type MetaInfoConnectionGroupProps = {
  connectionId: string;
  displayName: string;
  databases: DatabaseMetaInfo[];
  open: boolean;
  onToggle: () => void;
  isDatabaseOpen: (database: string) => boolean;
  onToggleDatabase: (database: string) => void;
  onRemoveLooseRel: (
    database: string,
    rel: LooseRelationship,
    label: string,
  ) => void;
  onRemoveJsonCol: (database: string, decl: JsonColumnDeclaration) => void;
  onRemoveEnumCol: (database: string, decl: EnumColumnDeclaration) => void;
  onRemoveSpatialCol: (database: string, decl: SpatialColumnDeclaration) => void;
};

export function MetaInfoConnectionGroup({
  connectionId,
  displayName,
  databases,
  open,
  onToggle,
  isDatabaseOpen,
  onToggleDatabase,
  onRemoveLooseRel,
  onRemoveJsonCol,
  onRemoveEnumCol,
  onRemoveSpatialCol,
}: MetaInfoConnectionGroupProps) {
  return (
    <div
      className="border rounded-md"
      data-testid={`metainfo-settings-conn-${connectionId}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium hover:bg-muted/50 rounded-md"
        aria-expanded={open}
        data-testid={`metainfo-settings-conn-toggle-${connectionId}`}
      >
        {open ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <span className="truncate" translate="no">
          {displayName}
        </span>
        <span className="text-xs text-muted-foreground ml-auto">
          {databases.length} database{databases.length === 1 ? '' : 's'}
        </span>
      </button>
      {open && (
        <div className="flex flex-col gap-2 px-3 pb-3">
          {databases.map((mi) => (
            <MetaInfoDatabaseCard
              key={mi.database}
              connectionId={connectionId}
              metaInfo={mi}
              open={isDatabaseOpen(mi.database)}
              onToggle={() => onToggleDatabase(mi.database)}
              onRemoveLooseRel={(rel, label) =>
                onRemoveLooseRel(mi.database, rel, label)
              }
              onRemoveJsonCol={(decl) => onRemoveJsonCol(mi.database, decl)}
              onRemoveEnumCol={(decl) => onRemoveEnumCol(mi.database, decl)}
              onRemoveSpatialCol={(decl) => onRemoveSpatialCol(mi.database, decl)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
